import React, { useEffect, useState } from "react";
import { PayloadAction } from "@reduxjs/toolkit";

import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import AlertContainer from "../../components/common/AlertContainer";
import SpinningCircle from "../../components/common/SpinningCircle";
import { Password } from "../../components/controls/Password";
import { SubmitButton } from "../../components/controls/SubmitButton";
import { useDispatch, useSelector } from "../../hooks";
import {
	connectAccounts,
	createAccountWithVippsData,
	getMatchingEmails,
} from "./slices/vipps.actions";

import { handleFieldValueChange, onAlertClose } from "./slices/vipps.slice";
import { useDebouncedCallback } from "use-debounce";
import { TYPE_DEBOUNCE } from "../settings/common.utils";
import { svgIcon } from "../../utils/svgProvider";

export const VippsConnectForm = () => {
	const dispatch = useDispatch();
	const { formatMessage } = useIntl();
	const { vippsId } = useParams();

	const isFetching = useSelector((state) => state.vippslogin.isFetching);
	const isCreating = useSelector((state) => state.vippslogin.isCreating);
	const alerts = useSelector((state) => state.vippslogin.alerts);
	const validationRules = useSelector(
		(state) => state.settings.validationRules,
	);
	const returnUrl = useSelector((state) => state.vippslogin.form.returnUrl);

	const schema = yup.object({
		email: yup
			.string()
			.min(2, "komid.register.validation.required")
			.max(70, "komid.register.validation.length.max")
			.required("komid.vipps.connect.email.error"),
		password: yup
			.string()
			.min(8, "komid.register.validation.required")
			.max(70, "komid.register.validation.length.max"),
	});

	const {
		handleSubmit,
		register,
		setValue,
		getValues,
		trigger,
		clearErrors,
		formState: { errors },
	} = useForm({
		mode: "onChange",
		reValidateMode: "onChange",
		resolver: yupResolver(schema),
	});

	const selectedEmail = getValues("email");
	const [emailList, setEmailList] = useState([]);

	useEffect(() => {
		dispatch(getMatchingEmails(vippsId)).then(
			(
				response: PayloadAction<{
					error?: string;
					isSuccess?: boolean;
					result?: { id: string; username: string }[];
				}>,
			) => {
				setEmailList(response.payload.result);
			},
		);
	}, [vippsId]);

	const handleDebounedFieldChange = useDebouncedCallback(async (payload) => {
		const isValid = await trigger(payload.field);

		if (isValid) {
			clearErrors(payload.field);
		}

		dispatch(handleFieldValueChange(payload));
	}, TYPE_DEBOUNCE);

	const onFieldValueChange = async (event) => {
		const { id, value } = event.target;

		setValue(id, value);

		if (event.type === "select") {
			const isValid = await trigger(id);

			if (isValid) {
				clearErrors(id);
			}

			dispatch(
				handleFieldValueChange({
					field: id,
					value,
				}),
			);
		} else {
			await handleDebounedFieldChange({
				field: id,
				value,
			});
		}
	};

	const connect = handleSubmit(async (values) => {
		const isValid = await trigger(["email", "password"]);

		if (isValid) {
			dispatch(
				connectAccounts({
					id: vippsId,
					usernameId: emailList.length === 1 ? emailList[0]?.id : values.email,
					password: values.password,
					returnUrl: returnUrl,
				}),
			);
		}
	});

	const createAccount = () => {
		dispatch(
			createAccountWithVippsData({
				id: vippsId,
				returnUrl,
			}),
		);
	};

	return (
		<>
			<AlertContainer
				alerts={alerts}
				onAlertClose={(data) => dispatch(onAlertClose(data))}
			/>
			<div className="form-wrapper">
				<section className="form-section">
					<h1>{formatMessage({ id: "komid.vipps.connect.title" })}</h1>
					<h3>
						{emailList?.length === 1 ? (
							formatMessage(
								{ id: "komid.vipps.connect.introduction" },
								{
									email: emailList[0].username,
									div: (str) => <div>{str}</div>,
									strong: (str) => <strong>{str}</strong>,
								},
							)
						) : (
							<div>
								<p
									className="form-text-translated"
									dangerouslySetInnerHTML={{
										__html: formatMessage({
											id: "komid.vipps.connect.introduction.several.email.hits",
										}),
									}}
								/>
								<br />
								<p
									className="form-text-translated"
									dangerouslySetInnerHTML={{
										__html: formatMessage({
											id: "komid.vipps.connect.info.several.email.hits",
										}),
									}}
								/>
								<div className="form-text-buttons">
									<input
										type="hidden"
										name="email"
										ref={register("email")?.ref}
									/>

									{emailList?.map((email, index) => (
										<label
											key={`email-${index}`}
											className="button tertiary"
											data-selected={selectedEmail === email.id}
											onClick={() => {
												onFieldValueChange({
													target: { id: "email", value: email.id },
													type: "select",
												});
											}}
										>
											<input
												key={`email-${index}`}
												type="radio"
												value={email.id}
												defaultChecked={selectedEmail === email.id}
											/>
											{selectedEmail === email.id
												? svgIcon.radioSelected
												: svgIcon.radioUnselected}{" "}
											<span>{email.username}</span>
										</label>
									))}
									{errors?.email?.message && (
										<span
											className="errorMessage"
											data-automationid="email.error"
										>
											<FormattedMessage
												id={errors?.email?.message.toString()}
												values={{
													fieldname: formatMessage({
														id: "komid.register.email",
													}),
												}}
											/>
										</span>
									)}
								</div>
							</div>
						)}
					</h3>
					<form data-hj-suppress onSubmit={connect}>
						<fieldset>
							<Password
								id="password"
								ref={register("password")?.ref}
								error={
									typeof errors.password?.message === "string"
										? errors.password.message
										: undefined
								}
								className="form-input"
								label={formatMessage({
									id: "komid.register.password",
								})}
								onChange={(event) => {
									onFieldValueChange(event);
								}}
								placeholder="******"
								maxLength={50}
								autoComplete="current-password"
							/>

							<div className="form-buttons">
								<SubmitButton
									data-automationid="vippsConnect.login"
									data-testid="vippsConnect.login"
									disabled={isFetching}
									isFetching={isFetching}
								>
									{formatMessage({ id: "komid.vipps.connect.linkaccount" })}
									{isFetching && <SpinningCircle className="loader" />}
								</SubmitButton>

								<div className="separator">
									<span>
										{formatMessage({ id: "komid.login.separatortext" })}
									</span>
								</div>
								<button
									className="button clear"
									type="button"
									data-automationid="vippsConnect.createNewAccount"
									data-testid="vippsConnect.createNewAccount"
									onClick={createAccount}
								>
									{formatMessage({ id: "komid.vipps.connect.createaccount" })}
									{isCreating && <SpinningCircle className="loader" />}
								</button>
							</div>
						</fieldset>
					</form>
				</section>
			</div>
		</>
	);
};
